/**
 * Pure, UI-side aggregation over worker-supplied RouteAnalyticsView rows, used
 * by the network overview and the route detail modal.
 *
 * The view has no RPK or per-cabin revenue field, so both are derived here and
 * labelled as such in the UI:
 *   - load factor is passengers / seats, and available seat miles is seats x
 *     statute miles, so RPK = availableSeatMiles x loadFactorPct / 100 exactly.
 *   - statute miles per route = availableSeatMiles / seats.
 * Nothing here invents a simulation number: every input comes from the view.
 */

import type {
  AirlineSeatShareView,
  CabinClass,
  RouteAnalyticsView,
  RouteSeatShareView,
} from '../../../domain/models/contracts';
import { SEAT_CLASSES } from '../../../domain/models';

/** Airline id the worker uses for the player in competitorShares. */
const PLAYER_AIRLINE_ID = 'PLAYER';

// ---------------------------------------------------------------------------
// Network totals
// ---------------------------------------------------------------------------

/** Revenue passenger-miles for one route, derived as ASM x load factor. */
export function revenuePassengerMilesOf(route: RouteAnalyticsView): number {
  return (route.availableSeatMiles * route.loadFactorPct) / 100;
}

/** Revenue per revenue passenger-mile, in US cents. Zero when nothing flew. */
export function yieldCentsPerRpm(revenueUSD: number, revenuePassengerMiles: number): number {
  return revenuePassengerMiles > 0 ? (revenueUSD / revenuePassengerMiles) * 100 : 0;
}

export interface NetworkSummary {
  readonly routeCount: number;
  readonly scheduledFlights: number;
  readonly operatedFlights: number;
  readonly passengers: number;
  readonly seats: number;
  readonly revenueUSD: number;
  readonly operatingCostUSD: number;
  readonly profitUSD: number;
  /** Available seat-miles (ASK). */
  readonly availableSeatMiles: number;
  /** Revenue passenger-miles (RPK), derived. */
  readonly revenuePassengerMiles: number;
  /** Passengers / seats across the network, 0-100. */
  readonly loadFactorPct: number;
  readonly yieldCentsPerRpm: number;
}

export function summarizeRoutes(routes: readonly RouteAnalyticsView[]): NetworkSummary {
  let scheduledFlights = 0;
  let operatedFlights = 0;
  let passengers = 0;
  let seats = 0;
  let revenueUSD = 0;
  let operatingCostUSD = 0;
  let availableSeatMiles = 0;
  let revenuePassengerMiles = 0;

  for (const route of routes) {
    scheduledFlights += route.scheduledFlights;
    operatedFlights += route.operatedFlights;
    passengers += route.passengers;
    seats += route.seats;
    revenueUSD += route.revenueUSD;
    operatingCostUSD += route.operatingCostUSD;
    availableSeatMiles += route.availableSeatMiles;
    revenuePassengerMiles += revenuePassengerMilesOf(route);
  }

  return {
    routeCount: routes.length,
    scheduledFlights,
    operatedFlights,
    passengers,
    seats,
    revenueUSD,
    operatingCostUSD,
    profitUSD: revenueUSD - operatingCostUSD,
    availableSeatMiles,
    revenuePassengerMiles,
    loadFactorPct: seats > 0 ? (passengers / seats) * 100 : 0,
    yieldCentsPerRpm: yieldCentsPerRpm(revenueUSD, revenuePassengerMiles),
  };
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

export type RouteSortKey = 'PROFIT' | 'REVENUE' | 'LOAD_FACTOR' | 'YIELD';

export const ROUTE_SORT_LABELS: Readonly<Record<RouteSortKey, string>> = {
  PROFIT: 'Profit',
  REVENUE: 'Revenue',
  LOAD_FACTOR: 'Load factor',
  YIELD: 'Yield',
};

export interface RankedRoute {
  readonly route: RouteAnalyticsView;
  readonly profitUSD: number;
  readonly revenuePassengerMiles: number;
  readonly yieldCents: number;
}

function sortValue(row: RankedRoute, key: RouteSortKey): number {
  switch (key) {
    case 'PROFIT':
      return row.profitUSD;
    case 'REVENUE':
      return row.route.revenueUSD;
    case 'LOAD_FACTOR':
      return row.route.loadFactorPct;
    case 'YIELD':
      return row.yieldCents;
  }
}

/** Routes sorted high to low by the chosen measure; ties break on leg id. */
export function rankRoutes(routes: readonly RouteAnalyticsView[], key: RouteSortKey): readonly RankedRoute[] {
  const rows: RankedRoute[] = routes.map((route) => {
    const rpm = revenuePassengerMilesOf(route);
    return {
      route,
      profitUSD: route.revenueUSD - route.operatingCostUSD,
      revenuePassengerMiles: rpm,
      yieldCents: yieldCentsPerRpm(route.revenueUSD, rpm),
    };
  });
  return rows.sort((a, b) => sortValue(b, key) - sortValue(a, key) || a.route.legId.localeCompare(b.route.legId));
}

// ---------------------------------------------------------------------------
// Scope helpers (which routes a hub or aircraft owns)
// ---------------------------------------------------------------------------

export function routesTouchingAirport(
  routes: readonly RouteAnalyticsView[],
  airportId: string,
): readonly RouteAnalyticsView[] {
  return routes.filter((route) => route.origin === airportId || route.destination === airportId);
}

export function routesFlownByAircraft(
  routes: readonly RouteAnalyticsView[],
  registration: string,
): readonly RouteAnalyticsView[] {
  return routes.filter((route) => route.aircraftReg === registration);
}

// ---------------------------------------------------------------------------
// Cabin yield and fares
// ---------------------------------------------------------------------------

interface WeightedSample {
  readonly value: number;
  readonly weight: number;
}

function weightedMean(samples: readonly WeightedSample[]): number | null {
  let total = 0;
  let weight = 0;
  for (const sample of samples) {
    if (sample.weight > 0 && Number.isFinite(sample.value)) {
      total += sample.value * sample.weight;
      weight += sample.weight;
    }
  }
  return weight > 0 ? total / weight : null;
}

export interface CabinRow {
  readonly cabin: CabinClass;
  /** Seats installed in this cabin across the routes in scope (per departure). */
  readonly seatsInstalled: number;
  /** Seat-weighted average fare on offer, USD. Null when the cabin is not sold. */
  readonly fareUSD: number | null;
  /** Fare per statute mile, US cents, seat-weighted. */
  readonly fareCentsPerMile: number | null;
  /** Seat-weighted market reference fare, USD. */
  readonly marketFareUSD: number | null;
  /** Our fare relative to the market reference, in percent (0 = equal). */
  readonly fareVsMarketPct: number | null;
}

export function cabinRows(routes: readonly RouteAnalyticsView[]): readonly CabinRow[] {
  return SEAT_CLASSES.map((cabin): CabinRow => {
    const sold = routes.filter((route) => route.seatsByCabin[cabin] > 0);
    const seatsInstalled = sold.reduce((sum, route) => sum + route.seatsByCabin[cabin], 0);
    const fareUSD = weightedMean(
      sold.map((route) => ({ value: route.currentFares[cabin], weight: route.seatsByCabin[cabin] })),
    );
    const fareCentsPerMile = weightedMean(
      sold.flatMap((route) => {
        const miles = route.seats > 0 ? route.availableSeatMiles / route.seats : 0;
        return miles > 0
          ? [{ value: (route.currentFares[cabin] / miles) * 100, weight: route.seatsByCabin[cabin] }]
          : [];
      }),
    );
    const marketFareUSD = weightedMean(
      sold.map((route) => ({ value: route.marketBaselineFares[cabin], weight: route.seatsByCabin[cabin] })),
    );
    return {
      cabin,
      seatsInstalled,
      fareUSD,
      fareCentsPerMile,
      marketFareUSD,
      fareVsMarketPct:
        fareUSD !== null && marketFareUSD !== null && marketFareUSD > 0
          ? (fareUSD / marketFareUSD - 1) * 100
          : null,
    };
  });
}

// ---------------------------------------------------------------------------
// Price elasticity curves
// ---------------------------------------------------------------------------

/** Fare as a percentage of the current fare, from 70% to 130% in 5-point steps. */
export const ELASTICITY_FARE_INDEX_PCTS: readonly number[] = [
  70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130,
];

export interface ElasticityPoint {
  readonly fareIndexPct: number;
  /** Demand change versus today's demand, in percent. */
  readonly demandChangePct: number;
}

export interface ElasticityCurve {
  readonly cabin: CabinClass;
  /** Seat-weighted own-price elasticity across the routes in scope (negative). */
  readonly elasticity: number;
  readonly points: readonly ElasticityPoint[];
}

/**
 * Constant-elasticity curve around the current fare: demand ∝ fare^ε, so the
 * demand change at fare ratio r is r^ε - 1. This matches the form used by
 * assessPriceSensitivity in routeMetrics.ts. Cabins that are not sold are left out.
 */
export function elasticityCurves(routes: readonly RouteAnalyticsView[]): readonly ElasticityCurve[] {
  const curves: ElasticityCurve[] = [];
  for (const cabin of SEAT_CLASSES) {
    const elasticity = weightedMean(
      routes.map((route) => ({
        value: route.priceElasticityByCabin[cabin],
        weight: route.seatsByCabin[cabin],
      })),
    );
    if (elasticity === null) continue;
    curves.push({
      cabin,
      elasticity,
      points: ELASTICITY_FARE_INDEX_PCTS.map((fareIndexPct) => ({
        fareIndexPct,
        demandChangePct: (Math.pow(fareIndexPct / 100, elasticity) - 1) * 100,
      })),
    });
  }
  return curves;
}

// ---------------------------------------------------------------------------
// Competitors
// ---------------------------------------------------------------------------

export interface CompetitorRow {
  readonly route: RouteAnalyticsView;
  readonly competitorCount: number;
  readonly competitorLoadFactorPct: number | null;
  readonly loadFactorGapPts: number | null;
  /** Player share of published seats on this O&D market, 0-100. Null when not reported. */
  readonly playerSeatSharePct: number | null;
  /** Largest single rival share of published seats, 0-100. Null when not reported. */
  readonly largestRivalSharePct: number | null;
}

function seatSharesFor(
  shares: readonly RouteSeatShareView[],
  origin: string,
  destination: string,
): readonly AirlineSeatShareView[] | null {
  const routeKey = `${origin}-${destination}`;
  return shares.find((entry) => entry.routeKey === routeKey)?.shares ?? null;
}

export function competitorRows(
  routes: readonly RouteAnalyticsView[],
  shares: readonly RouteSeatShareView[],
): readonly CompetitorRow[] {
  return routes.map((route): CompetitorRow => {
    const routeShares = seatSharesFor(shares, route.origin, route.destination);
    const player = routeShares?.find((entry) => entry.airlineId === PLAYER_AIRLINE_ID);
    const rivals = routeShares?.filter((entry) => entry.airlineId !== PLAYER_AIRLINE_ID) ?? [];
    const largestRival = rivals.reduce<number | null>(
      (max, entry) => (max === null || entry.seatSharePct > max ? entry.seatSharePct : max),
      null,
    );
    return {
      route,
      competitorCount: route.competitorCount,
      competitorLoadFactorPct: route.competitorLoadFactorPct,
      loadFactorGapPts: route.loadFactorGapPts,
      playerSeatSharePct: player?.seatSharePct ?? null,
      largestRivalSharePct: largestRival,
    };
  });
}
